/* IS-05 Care Plan — .pick-list.add-cart .type-careplan
 * 썸네일은 64×64, object-fit: contain (가이드 CSS). 상품명 2줄, 가격 1줄.
 * Add to Basket 은 본품이 장바구니에 있을 때만. */

import { useEffect } from "react";
import { ctaTranslate } from "../lib/ctaTranslate";
import {
  fallbackProductImage,
  handleProductImageError,
  normalizeProductImage,
} from "../lib/fallback-image";
import { useLabel } from "../lib/labels";
import { formatMoney } from "../lib/money";
import { ProductNameText } from "../lib/ProductNameText";
import { postToHost } from "../lib/postmessage";
import { Button } from "../ui";

interface CarePrice {
  amount?: number;
  symbol?: string;
}

interface CarePlan {
  id?: string;
  ew_sku?: string;
  name?: string;
  image?: string;
  price?: CarePrice | null;
}

interface CarePlanData {
  sku?: string;
  name?: string;
  show_add?: boolean;
  plans?: CarePlan[];
  pbp_cta?: boolean;
  metadata?: { pbp_url?: string };
}

interface Props {
  data: CarePlanData;
  sessionToken: string;
}

function planPrice(price: CarePrice | null | undefined): string {
  const amount = price?.amount;
  if (amount == null || !Number.isFinite(amount)) return "";
  return formatMoney(amount, price?.symbol || "£");
}

export default function CarePlanCard({ data, sessionToken }: Props) {
  const plans = (data.plans || []).filter((plan) => plan?.id);
  const addLabel = useLabel("addToBasket", "Add to Basket");

  const mainSku = data.sku || "";
  const showAdd = Boolean(data.show_add);

  useEffect(() => {
    const report = () => {
      const root = document.querySelector(".ai-gp1--section") as HTMLElement | null;
      if (!root) return;
      postToHost(
        "widget.resize",
        {
          width: Math.ceil(root.getBoundingClientRect().width),
          height: Math.ceil(root.scrollHeight),
        },
        { sessionId: sessionToken },
      );
    };
    report();
    const timer = window.setTimeout(report, 120);
    return () => window.clearTimeout(timer);
  }, [plans, showAdd, sessionToken]);

  if (!plans.length) return null;

  const addPlan = (plan: CarePlan) => {
    const ewSku = (plan.ew_sku || "").trim();
    if (!ewSku) return;
    const name = plan.name || mainSku;
    void ctaTranslate({
      code: "recommend.add_1x_to_my_cart",
      params: { name, sku: mainSku },
      sessionToken,
      fallback: `Add ${name} with ${mainSku} to my basket`,
      toolCall: {
        tool: "commerce_add_care_plan",
        args: { sku: mainSku, package_item_id: ewSku },
      },
    });
  };

  return (
    <div className="ai-gp1--section">
      <ul className="pick-list add-cart">
        {plans.map((plan) => (
          <li className="pick-item" key={plan.id}>
            <div className="pick-item--inner type-careplan">
              <div className="pick-product">
                <div className="product-img">
                  <img
                    src={plan.image ? normalizeProductImage(plan.image) : fallbackProductImage()}
                    alt={plan.name || "care plan"}
                    loading="lazy"
                    draggable={false}
                    onError={handleProductImageError}
                  />
                </div>
                <div className="product-inner">
                  <div className="product-ufn">
                    <ProductNameText name={plan.name} sku={mainSku} />
                  </div>
                  {planPrice(plan.price) ? (
                    <div className="product-price">
                      <div className="price-purchase">{planPrice(plan.price)}</div>
                    </div>
                  ) : null}
                </div>
              </div>
              {showAdd ? (
                <div className="ac-btn-wrap">
                  <Button variant="primary" size="xs" onClick={() => addPlan(plan)}>
                    {addLabel}
                  </Button>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {/* 18_제품_Insurance §4: Go to Buying Page는 호스트 linkout 칩으로 일원화되어 카드 내부에는 렌더하지 않음. */}
    </div>
  );
}
